import { FC, useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import Loader from '@components/elements/Loader';
import {
  createCompanyProfile,
  companyRegisterDomain,
} from '@sdk/managers/UsersManager';

const FORM_SCHEMA = z.object({
  name: z.string().nonempty(),
  domain: z
    .string()
    .nonempty()
    .regex(
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.([a-zA-Z]{1,6})(\.[a-z]{2,3})?$/,
      'Invalid format'
    ),
});

type FormInput = z.infer<typeof FORM_SCHEMA>;

const CompanySetupPage: FC = () => {
  const history = useHistory<any>();

  const { dbUser, refetch, company, loading } = useAuth();

  useEffect(() => {
    if (!dbUser) history.push('/login');
  }, [dbUser, history]);

  useEffect(() => {
    if (loading === false && company !== undefined) {
      history.push('/');
    }
  }, [company, loading, history]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormInput>({
    mode: 'onChange',
    resolver: zodResolver(FORM_SCHEMA),
    shouldUnregister: false,
  });

  const onSubmit = useCallback(
    async (data: FormInput) => {
      try {
        const dbPayload: any = {
          name: data.name,
          domain: data.domain,
        };
        console.log('calling setUpProfile');

        const res = (await createCompanyProfile(
          dbUser?.id,
          dbUser?.username,
          dbPayload
        )) as {
          data?: {
            result?: {
              companyID: string;
            };
          };
        };
        if (res?.data?.result) {
          try {
            await companyRegisterDomain(
              res.data.result.companyID,
              dbPayload.domain
            );
          } catch (err: any) {
            console.error('companyRegisterDomain err', err);
            toast.error(
              'Failed to set company domain auto approval. ' + err?.message ||
                err?.errors?.[0]?.message
            );
          }
        }

        await refetch();
        toast.success('Successfully setup your profile.');
        history.replace('/');
      } catch (err: any) {
        console.error('createCompanyProfile err', err);
        toast.error(err?.message || 'Failed to setup profile');
      }
    },
    [dbUser, history, refetch]
  );

  const content = useMemo(() => {
    if (isSubmitting)
      return (
        <Loader
          title="Setting up your profile..."
          description="This may take a few seconds, please don't close this page."
        />
      );
    return (
      <form
        className="space-y-4"
        onSubmit={handleSubmit(onSubmit, (err) => {
          if (err.domain)
            return toast.error('Domain error. ' + err.domain.message);
          if (err.name)
            return toast.error('Company Name error. ' + err.name.message);
          else {
            toast.error('Validation error');
          }
          console.log(err);
        })}
      >
        <div>
          <InputText
            placeholder="Company Name"
            shadowColor="#FFB400"
            borderColor="#00cf49"
            {...register('name')}
          />
          {errors.name && (
            <p className="mt-2 text-right text-red">*{errors.name.message}</p>
          )}
        </div>

        <div>
          <InputText
            placeholder="Company Domain (e.g. gmail.com)"
            shadowColor="#FFB400"
            borderColor="#00cf49"
            {...register('domain')}
          />
          <div className="mt-4">
            <p className="text-left gray-dark">
              E.g. Company Domain: <br />
              John@megax.world <br />
              <br />
              Domain: <br />
              megax.world
            </p>
            {errors.domain && (
              <p className="text-right text-red">*{errors.domain.message}</p>
            )}
          </div>
        </div>

        <div className="signup-action-container">
          <ButtonMighty
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            color="#4400FF"
            text="Create profile"
            borderColor="#ff1dfc"
            margin="0"
            textColor="#fff"
          />
        </div>
      </form>
    );
  }, [
    isSubmitting,
    handleSubmit,
    onSubmit,
    register,
    errors.name,
    errors.domain,
  ]);
  return (
    <div className="min-h-screen pt-20 poll-x-pattern">
      <div className="container mx-auto md:w-108">
        <div className="setup-container">
          <h1 className="mb-8 text-6xl font-bold">Setup Your Company</h1>
          <div>{content}</div>
        </div>
      </div>
    </div>
  );
};

export default CompanySetupPage;
